import React, { useMemo, useState } from 'react';
import Button from '../../components/Button';
import { __ } from '../../core/utils';
import * as _ from 'lodash';
import styles from '../../styles/forms/create_folder_wizard/CreateFolderForm.scss';
import CheckedIcon from '../../assets/images/checked-15x15.svg';
import UncheckedIcon from '../../assets/images/unchecked-15x15.svg';
import NeutralIcon from '../../assets/images/neutral-15x15.svg';
import FolderClosedIcon from '../../assets/images/folder-closed-16x16.svg';
import FolderOpenedIcon from '../../assets/images/folder-opened-16x16.svg';
import classNames from 'classnames';

const getInitialStatus = (folder, modelIds, folderModelKey) => {
  if (folderModelKey === 'selected_count') {
    const selectedCount = _.toNumber(folder.selected_count || 0);

    if (selectedCount === 0) {
      return 'unchecked';
    }

    return selectedCount === _.size(modelIds) ? 'checked' : 'neutral';
  }

  const folderModelIds = _.map(folder[folderModelKey] || [], _.toString);
  const selectedModelIds = _.map(modelIds, _.toString);
  const matchingModelIds = _.intersection(folderModelIds, selectedModelIds);

  if (_.isEmpty(matchingModelIds)) {
    return 'unchecked';
  }

  return _.size(matchingModelIds) === _.size(selectedModelIds)
    ? 'checked'
    : 'neutral';
};

const nextStatus = (status) => {
  if (status === 'checked') {
    return 'unchecked';
  }

  if (status === 'unchecked') {
    return 'checked';
  }

  return 'checked';
};

const AttachFilesToFoldersForm = ({
  folders = [],
  plan_ids = [],
  file_ids = [],
  folder_model_key = 'plan_ids',
  singleAction = false,
  onClose,
  onSubmit,
  submitting,
}) => {
  const model_ids = _.isEmpty(file_ids) ? plan_ids : file_ids;
  const initialStatuses = useMemo(() => {
    const statuses = {};

    const collectStatuses = (items) => {
      _.each(items, (folder) => {
        statuses[folder.id] = getInitialStatus(
          folder,
          model_ids,
          folder_model_key
        );
        collectStatuses(folder.children || []);
      });
    };

    collectStatuses(folders);

    return statuses;
  }, [folders, model_ids, folder_model_key]);

  const [statuses, setStatuses] = useState(initialStatuses);
  const [expanded, setExpanded] = useState({});

  const getStatus = (folder) => statuses[folder.id] || 'unchecked';

  const toggleStatus = (folder) => {
    if (singleAction) {
      setStatuses({
        ..._.mapValues(statuses, () => 'unchecked'),
        [folder.id]: getStatus(folder) === 'checked' ? 'unchecked' : 'checked',
      });
      return;
    }

    setStatuses({
      ...statuses,
      [folder.id]: nextStatus(getStatus(folder)),
    });
  };

  const toggleExpanded = (folder) => {
    setExpanded({
      ...expanded,
      [folder.id]: expanded[folder.id] === false,
    });
  };

  const renderCheckbox = (folder) => {
    const status = getStatus(folder);
    const Icon =
      status === 'checked'
        ? CheckedIcon
        : status === 'neutral'
        ? NeutralIcon
        : UncheckedIcon;

    return (
      <Icon
        className={classNames(
          styles.checkbox,
          status === 'checked' && styles.checked,
          status === 'neutral' && styles.neutral
        )}
        onClick={(e) => {
          e.stopPropagation();
          toggleStatus(folder);
        }}
      />
    );
  };

  const renderFolder = (folder, level = 0) => {
    const isExpanded = expanded[folder.id] !== false;
    const hasChildren = !_.isEmpty(folder.children);
    const FolderIcon =
      hasChildren && isExpanded ? FolderOpenedIcon : FolderClosedIcon;

    return (
      <React.Fragment key={folder.id}>
        <div
          className={styles.folder}
          style={{ paddingLeft: 20 + level * 20 }}
          onClick={() => toggleStatus(folder)}
        >
          <div
            className={styles.folderContent}
            onClick={(e) => {
              e.stopPropagation();
              hasChildren && toggleExpanded(folder);
            }}
          >
            <FolderIcon className={styles.folderIcon} />
            <span>{folder.title}</span>
          </div>
          {renderCheckbox(folder)}
        </div>
        {hasChildren &&
          isExpanded &&
          _.map(folder.children, (child) => renderFolder(child, level + 1))}
      </React.Fragment>
    );
  };

  const handleSubmit = () => {
    const folder_ids = [];
    const removed_folder_ids = [];

    _.each(statuses, (status, folderId) => {
      const initialStatus = initialStatuses[folderId] || 'unchecked';

      if (status === 'checked' && initialStatus !== 'checked') {
        folder_ids.push(folderId);
      }

      if (status === 'unchecked' && initialStatus !== 'unchecked') {
        removed_folder_ids.push(folderId);
      }
    });

    onSubmit({ folder_ids, removed_folder_ids });
  };

  return (
    <form onSubmit={handleSubmit} className={styles.wrapper}>
      <div className={styles.folderChooser}>
        {_.map(folders, (folder) => renderFolder(folder))}
      </div>
      <div className={styles.footer}>
        <Button
          lightGray
          medium
          left
          middleText={__('button.cancel')}
          onClick={onClose}
        />
        <Button
          lightBlue
          medium
          middleText={singleAction ? __('button.move-to') : __('button.add')}
          spinning={submitting}
          disabled={singleAction && !_.includes(_.values(statuses), 'checked')}
          onClick={handleSubmit}
        />
      </div>
    </form>
  );
};

export default AttachFilesToFoldersForm;
